<template>
  <div v-if="show" class="mdl-overlay" @click.self="$emit('close')">
    <div class="mdl-card topup-modal-card shadow-lg">
      <!-- Modal Header (fixed at top) -->
      <div class="topup-modal-header">
        <div class="d-flex align-items-center gap-3">
          <div class="topup-icon-badge">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="2" y="5" width="20" height="14" rx="2"/>
              <line x1="2" y1="10" x2="22" y2="10"/>
            </svg>
          </div>
          <div>
            <h3 class="topup-modal-title mb-0">Egyenleg Feltöltés</h3>
            <small class="topup-modal-subtitle">Befizetés rögzítése és csoport ellenőrzése</small>
          </div>
        </div>
        <button type="button" class="modal-close-btn" @click="$emit('close')" aria-label="Bezárás">✕</button>
      </div>

      <form @submit.prevent="handleSubmit" class="topup-modal-form">
        <!-- Scrollable Body Container -->
        <div class="topup-modal-body">
          <!-- Double Check Dancer Verification Card -->
          <div class="topup-verify-card mb-3">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <span class="topup-verify-title">Kiválasztott táncos ellenőrzése</span>
              <span class="topup-id-pill">ID: #{{ childId }}</span>
            </div>
            <div class="d-flex align-items-center gap-3">
              <div class="verify-avatar">
                <span>{{ (childInfo && childInfo.childxfamily_name) ? childInfo.childxfamily_name.charAt(0) : 'T' }}</span>
              </div>
              <div class="flex-grow-1 min-w-0">
                <div class="topup-dancer-name text-truncate">
                  {{ (childInfo && childInfo.childxfamily_name) ? (childInfo.childxfamily_name + ' ' + (childInfo.childxsure_name || '')) : 'Táncos' }}
                </div>
                <div class="topup-dancer-balance">
                  Jelenlegi egyenleg: 
                  <span class="fw-bold" :class="balance < 0 ? 'topup-balance--neg' : 'text-muted'">
                    {{ balance < 0 ? formatHuf(balance) : '0 Ft' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Assigned groups -->
            <div v-if="childGroups && childGroups.length" class="mt-2.5 pt-2 topup-card-divider">
              <div class="topup-groups-label mb-1">Hozzárendelt csoportok:</div>
              <div class="d-flex flex-wrap gap-1.5">
                <span v-for="grp in childGroups" :key="grp.id" class="group-badge-tag">
                  {{ grp.group_name }} <span v-if="grp.is_primary" class="text-primary font-weight-bold">★</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Group Selection -->
          <div class="topup-field-group mb-3">
            <label class="topup-label d-flex justify-content-between align-items-center mb-1.5">
              <span>Érintett csoport <span class="text-muted font-normal small">(opcionális)</span></span>
              <span v-if="form.group_id" class="topup-group-selected-badge">Kiválasztva</span>
            </label>
            <select
              v-model.number="form.group_id"
              class="topup-select"
            >
              <option :value="0">Általános egyenleg (Nincs csoport)</option>
              <option
                v-for="grp in childGroups"
                :key="grp.group_id"
                :value="grp.group_id"
              >
                {{ grp.group_name }} {{ grp.is_primary ? '(Elsődleges csoport)' : '' }}
              </option>
            </select>
            <div class="topup-help-text mt-1">
              Ha a befizetés egy konkrét kurzushoz tartozik, válaszd ki fent.
            </div>
          </div>

          <!-- Preset Quick Amount Buttons -->
          <div class="topup-field-group mb-3">
            <label class="topup-label mb-1.5">Gyors összegek</label>
            <div class="preset-pill-container">
              <button
                v-for="preset in [5000, 10000, 15000, 20000, 30000]"
                :key="preset"
                type="button"
                class="preset-pill-btn"
                :class="{ active: form.amount === preset }"
                @click="form.amount = preset"
              >
                {{ formatHuf(preset) }}
              </button>
              <button
                v-if="monthlyFee > 0"
                type="button"
                class="preset-pill-btn preset-pill-btn--monthly"
                :class="{ active: form.amount === monthlyFee }"
                @click="form.amount = monthlyFee"
              >
                Havi díj ({{ formatHuf(monthlyFee) }})
              </button>
            </div>
          </div>

          <!-- Amount input with currency suffix -->
          <div class="topup-field-group mb-3">
            <label class="topup-label mb-1.5">
              Befizetendő összeg <span class="text-danger">*</span>
            </label>
            <div class="topup-input-group">
              <input
                type="number"
                v-model.number="form.amount"
                class="topup-input"
                placeholder="Pl. 15000"
                min="1"
                step="1"
                required
              />
              <span class="topup-addon">HUF</span>
            </div>
          </div>

          <!-- Payment method -->
          <div class="topup-field-group mb-3">
            <label class="topup-label mb-1.5">Fizetési mód</label>
            <select v-model="form.method" class="topup-select">
              <option value="készpénz">Készpénz</option>
              <option value="átutalás">Banki átutalás</option>
              <option value="bankkártya">Bankkártya</option>
              <option value="egyéb">Egyéb / Korrekció</option>
            </select>
          </div>

          <!-- Note -->
          <div class="topup-field-group mb-3">
            <label class="topup-label mb-1.5">Megjegyzés <span class="text-muted font-normal small">(opcionális)</span></label>
            <textarea
              v-model="form.note"
              class="topup-textarea"
              rows="2"
              placeholder="Pl.: márciusi tagdíj készpénzben átadva az edzésen"
            ></textarea>
          </div>

          <!-- Security Confirmation Preview Banner -->
          <div v-if="form.amount && form.amount > 0" class="security-confirm-box mb-2">
            <div class="d-flex align-items-center gap-2">
              <span class="security-icon d-flex align-items-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </span>
              <div class="security-text">
                <div class="security-title">Megerősítés összegzése</div>
                <div>
                  Jóváírásra kerül: <span class="security-amount">{{ formatHuf(form.amount) }}</span> 
                  a következő táncosnak: <span class="security-dancer">{{ (childInfo && childInfo.childxfamily_name) ? (childInfo.childxfamily_name + ' ' + childInfo.childxsure_name) : 'Táncos' }}</span>
                  <span v-if="selectedGroupName" class="security-group"> ({{ selectedGroupName }})</span>.
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer (pinned at bottom) -->
        <div class="topup-modal-footer">
          <button type="button" class="modern-btn modern-btn--secondary btn-sm px-3" @click="$emit('close')">Mégse</button>
          <button type="submit" class="modern-btn modern-btn--primary btn-sm px-4" :disabled="isSaving || !form.amount || form.amount <= 0">
            <span v-if="isSaving" class="spinner-border spinner-border-sm me-1" role="status"></span>
            {{ isSaving ? 'Feltöltés...' : 'Befizetés rögzítése' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChildTopUpModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    childInfo: {
      type: Object,
      default: null
    },
    childId: {
      type: [String, Number],
      default: ''
    },
    childGroups: {
      type: Array,
      default: () => []
    },
    balance: {
      type: Number,
      default: 0
    },
    monthlyFee: {
      type: Number,
      default: 0
    },
    isSaving: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'submit'],
  data() {
    return {
      form: {
        amount: null,
        method: 'készpénz',
        note: '',
        group_id: 0
      }
    };
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.resetForm();
      }
    }
  },
  computed: {
    selectedGroupName() {
      if (!this.form.group_id) return '';
      const grp = this.childGroups.find(g => g.group_id === this.form.group_id);
      return grp ? grp.group_name : '';
    }
  },
  methods: {
    formatHuf(v) {
      return (v || 0).toLocaleString('hu-HU') + ' HUF';
    },
    resetForm() {
      const defaultGroup = this.childGroups && this.childGroups.length ? (this.childGroups[0].group_id || 0) : 0;
      this.form = {
        amount: null,
        method: 'készpénz',
        note: '',
        group_id: defaultGroup
      };
    },
    handleSubmit() {
      if (!this.form.amount || this.form.amount <= 0) return;
      this.$emit('submit', { ...this.form });
    }
  }
};
</script>

<style scoped>
.mdl-overlay {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  padding: 1.5rem 1rem;
  z-index: 99999;
}

.topup-modal-card {
  max-width: 520px;
  width: min(520px, 94vw);
  max-height: calc(100vh - 3rem);
  display: flex;
  flex-direction: column;
  background: var(--card-bg) !important;
  color: var(--color-text) !important;
  border: 1px solid var(--color-border) !important;
  border-radius: 20px !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.45) !important;
  position: relative;
  overflow: hidden !important;
  margin: auto;
}

.topup-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--color-border);
  background: var(--card-bg);
  flex-shrink: 0;
}

.topup-modal-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--color-heading);
}

.topup-modal-subtitle {
  font-size: 0.8rem;
  color: var(--color-text-muted);
}

.topup-icon-badge {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--primary-blue, #2563eb), var(--secondary-blue, #0ea5e9));
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
}

.modal-close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--color-background-soft) !important;
  color: var(--color-text-muted) !important;
  border: 1px solid var(--color-border) !important;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  font-size: 0.875rem;
}

.modal-close-btn:hover {
  background: rgba(239, 68, 68, 0.15) !important;
  color: #ef4444 !important;
  border-color: rgba(239, 68, 68, 0.3) !important;
}

.topup-modal-form {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.topup-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem 1.5rem;
}

.topup-modal-body::-webkit-scrollbar {
  width: 6px;
}
.topup-modal-body::-webkit-scrollbar-track {
  background: transparent;
}
.topup-modal-body::-webkit-scrollbar-thumb {
  background: var(--color-border);
  border-radius: 4px;
}

.topup-verify-card {
  background: var(--color-background-soft);
  border: 1px solid var(--color-border);
  border-left: 4px solid var(--primary-blue, #2563eb);
  border-radius: 12px;
  padding: 0.875rem 1rem;
}

.topup-verify-title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.topup-id-pill {
  background: rgba(37, 99, 235, 0.12);
  color: var(--primary-blue, #2563eb);
  border: 1px solid rgba(37, 99, 235, 0.25);
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 20px;
  padding: 2px 8px;
}

.verify-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--card-bg);
  border: 1.5px solid var(--color-border);
  color: var(--primary-blue, #2563eb);
  font-weight: 700;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.topup-dancer-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--color-heading);
}

.topup-dancer-balance {
  font-size: 0.8rem;
  color: var(--color-text-muted);
}

.topup-balance--pos {
  color: #10b981;
}

.topup-balance--neg {
  color: #ef4444;
}

.topup-card-divider {
  border-top: 1px solid var(--color-border);
}

.topup-groups-label {
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.group-badge-tag {
  background: var(--card-bg);
  color: var(--color-heading);
  border: 1px solid var(--color-border);
  padding: 0.2rem 0.6rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
}

.topup-field-group {
  position: relative;
}

.topup-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-heading);
}

.topup-group-selected-badge {
  background: rgba(37, 99, 235, 0.1);
  color: var(--primary-blue, #2563eb);
  border: 1px solid rgba(37, 99, 235, 0.2);
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 6px;
  padding: 2px 8px;
}

.topup-select,
.topup-textarea {
  width: 100%;
  background: var(--card-bg);
  color: var(--color-text);
  border: 1.5px solid var(--color-border);
  border-radius: 10px;
  padding: 0.65rem 0.875rem;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  outline: none;
  font-family: inherit;
}

.topup-select:focus,
.topup-textarea:focus {
  border-color: var(--primary-blue, #2563eb) !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15) !important;
}

.topup-select option {
  background: var(--card-bg);
  color: var(--color-text);
}

.topup-help-text {
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.preset-pill-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.preset-pill-btn {
  background: var(--color-background-soft);
  color: var(--color-text);
  border: 1.5px solid var(--color-border);
  border-radius: 20px;
  padding: 0.35rem 0.85rem;
  font-size: 0.825rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.preset-pill-btn:hover {
  border-color: var(--primary-blue, #2563eb);
  color: var(--primary-blue, #2563eb);
  background: rgba(37, 99, 235, 0.08);
}

.preset-pill-btn.active {
  background: var(--primary-blue, #2563eb) !important;
  color: #ffffff !important;
  border-color: var(--primary-blue, #2563eb) !important;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
}

.preset-pill-btn--monthly {
  border-color: rgba(16, 185, 129, 0.4);
  color: #10b981;
}

.preset-pill-btn--monthly:hover {
  background: rgba(16, 185, 129, 0.1);
  border-color: #10b981;
}

.preset-pill-btn--monthly.active {
  background: #10b981 !important;
  color: #ffffff !important;
  border-color: #10b981 !important;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.35);
}

/* Amount Input Group Fix - NO OVERFLOW */
.topup-input-group {
  display: flex;
  width: 100%;
  border-radius: 10px;
  border: 1.5px solid var(--color-border);
  overflow: hidden;
  background: var(--card-bg);
  transition: all 0.2s ease;
}

.topup-input-group:focus-within {
  border-color: var(--primary-blue, #2563eb);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.topup-input {
  flex: 1 1 auto;
  width: 1% !important;
  min-width: 0;
  background: transparent !important;
  color: var(--color-text) !important;
  border: none !important;
  padding: 0.65rem 0.875rem;
  font-size: 1rem;
  font-weight: 600;
  outline: none !important;
  box-shadow: none !important;
}

.topup-addon {
  flex-shrink: 0;
  background: var(--color-background-soft);
  color: var(--color-text-muted);
  font-weight: 700;
  font-size: 0.825rem;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1.5px solid var(--color-border);
}

.security-confirm-box {
  background: rgba(16, 185, 129, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.25);
  border-radius: 12px;
  padding: 0.875rem 1rem;
}

.security-icon {
  font-size: 1.15rem;
  flex-shrink: 0;
}

.security-text {
  font-size: 0.825rem;
  line-height: 1.45;
  color: var(--color-text);
}

.security-title {
  color: var(--color-heading);
  font-size: 0.85rem;
  font-weight: 700;
}

.security-amount {
  color: #10b981;
  font-weight: 700;
}

.security-dancer {
  color: var(--color-heading);
  font-weight: 600;
}

.security-group {
  color: var(--primary-blue-light, #3b82f6);
  font-weight: 600;
}

.topup-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid var(--color-border);
  background: var(--card-bg);
  flex-shrink: 0;
}

[data-theme="dark"] .topup-modal-card {
  background: var(--gray-800) !important;
  border-color: var(--gray-700) !important;
  color: var(--gray-100) !important;
}

[data-theme="dark"] .topup-modal-header {
  background: var(--gray-800) !important;
  border-bottom-color: var(--gray-700) !important;
}

[data-theme="dark"] .topup-modal-footer {
  background: var(--gray-800) !important;
  border-top-color: var(--gray-700) !important;
}

[data-theme="dark"] .topup-verify-card {
  background: var(--gray-900) !important;
  border-color: var(--gray-700) !important;
  border-left-color: var(--primary-blue-light) !important;
}

[data-theme="dark"] .verify-avatar {
  background: var(--gray-800) !important;
  border-color: var(--gray-700) !important;
  color: #60a5fa !important;
}

[data-theme="dark"] .topup-id-pill,
[data-theme="dark"] .topup-group-selected-badge {
  color: #93c5fd !important;
  background: rgba(59, 130, 246, 0.2) !important;
  border-color: rgba(59, 130, 246, 0.35) !important;
}

[data-theme="dark"] .group-badge-tag {
  background: var(--gray-800) !important;
  border-color: var(--gray-700) !important;
  color: var(--gray-200) !important;
}

[data-theme="dark"] .topup-select,
[data-theme="dark"] .topup-textarea {
  background: var(--gray-900) !important;
  color: var(--gray-100) !important;
  border-color: var(--gray-700) !important;
}

[data-theme="dark"] .topup-select option {
  background: var(--gray-900) !important;
  color: var(--gray-100) !important;
}

[data-theme="dark"] .topup-input-group {
  border-color: var(--gray-700) !important;
  background: var(--gray-900) !important;
}

[data-theme="dark"] .topup-addon {
  background: var(--gray-800) !important;
  color: var(--gray-400) !important;
  border-color: var(--gray-700) !important;
}

[data-theme="dark"] .security-confirm-box {
  background: rgba(16, 185, 129, 0.12) !important;
  border-color: rgba(16, 185, 129, 0.3) !important;
}

[data-theme="dark"] .security-group {
  color: #60a5fa !important;
}

[data-theme="dark"] .preset-pill-btn {
  background: var(--gray-900) !important;
  border-color: var(--gray-700) !important;
  color: var(--gray-200) !important;
}

[data-theme="dark"] .preset-pill-btn:hover {
  background: rgba(59, 130, 246, 0.15) !important;
  border-color: #60a5fa !important;
  color: #93c5fd !important;
}
</style>
